import { useState } from 'react'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import PredictInput from './PredictInput'

/**
 * Zone 5 — the check.
 *
 * The component holds your selections and nothing else. It does not know which
 * answer is right; the server never told it. Grading is one POST, and the
 * explanations arrive with the grade.
 */

interface Choice {
  id: string
  text: string
}
interface Question {
  id: string
  kind: string
  prompt: string
  choices: Choice[]
}
interface Feedback {
  questionId: string
  correct: boolean
  correctChoiceIds: string[]
  /** Predict questions only, and only ever present after grading. */
  expectedOutput?: string
  explanation: string
}
interface Result {
  score: number
  total: number
  passed: boolean
  maxCombo: number
  multiplier: number
  xpGranted: number
  leveledUp: boolean
  feedback: Feedback[]
  game: {
    totalXp: number
    level: number
    xpIntoLevel: number
    xpForNextLevel: number
    progress: number
    streak: number
    longestStreak: number
  }
}

export default function Quiz({ lessonId, questions }: { lessonId: string; questions: Question[] }) {
  const { user, game, setGame } = useAuth()
  const [selected, setSelected] = useState<Record<string, string[]>>({})
  // Predict-the-output answers are free text, so they need their own store.
  // Keeping them separate from `selected` rather than overloading it means the
  // submit builder can tell the two kinds apart without inspecting shapes.
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [result, setResult] = useState<Result | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (questions.length === 0) return null

  function toggle(questionId: string, choiceId: string) {
    setSelected((prev) => ({ ...prev, [questionId]: [choiceId] }))
  }

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      // Build from `questions`, not from the answer stores. Iterating the
      // stores would silently drop any question the learner left blank, and a
      // dropped question is one the server never grades and never counts
      // against them — a way to score 3/3 by answering three of four.
      const answers = questions.map((q) =>
        q.kind === 'predict'
          ? { questionId: q.id, text: typed[q.id] ?? '' }
          : { questionId: q.id, choiceIds: selected[q.id] ?? [] },
      )
      const res = await api<Result>('/quiz/submit', {
        method: 'POST',
        body: { lessonId, answers },
      })
      setResult(res)
      if (game) setGame({ ...game, ...res.game })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not submit your answers.')
    } finally {
      setBusy(false)
    }
  }

  const answeredAll = questions.every((q) =>
    q.kind === 'predict' ? (typed[q.id] ?? '').trim().length > 0 : (selected[q.id]?.length ?? 0) > 0,
  )
  const feedbackFor = (id: string) => result?.feedback.find((f) => f.questionId === id)

  return (
    <section className="zone zone-check">
      <h3 className="zone-heading">
        <span className="zone-num">5</span> Check
      </h3>

      {!user && (
        <p className="notice">
          Sign in to have your answers graded and counted — grading happens on the server, so
          there is nothing to grade without an account.
        </p>
      )}

      <ol className="questions">
        {questions.map((q, qi) => {
          const fb = feedbackFor(q.id)
          return (
            <li key={q.id} className="question">
              <p className="question-prompt">
                <span className="dim">{qi + 1}.</span> {q.prompt}
              </p>

              {q.kind === 'predict' ? (
                <PredictInput
                  id={q.id}
                  value={typed[q.id] ?? ''}
                  disabled={Boolean(result) || !user}
                  correct={fb?.correct}
                  expectedOutput={fb?.expectedOutput}
                  onChange={(next) => setTyped((prev) => ({ ...prev, [q.id]: next }))}
                />
              ) : (
              <div className="choices" role="radiogroup" aria-label={q.prompt}>
                {q.choices.map((ch) => {
                  const isSelected = selected[q.id]?.includes(ch.id) ?? false
                  const isRight = fb?.correctChoiceIds.includes(ch.id) ?? false
                  const cls = [
                    'choice',
                    isSelected ? 'choice-selected' : '',
                    fb && isRight ? 'choice-correct' : '',
                    fb && isSelected && !isRight ? 'choice-wrong' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')

                  return (
                    <button
                      key={ch.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={cls}
                      disabled={Boolean(result) || !user}
                      onClick={() => toggle(q.id, ch.id)}
                    >
                      {ch.text}
                    </button>
                  )
                })}
              </div>
              )}

              {fb && (
                <p className={fb.correct ? 'explain explain-ok' : 'explain explain-no'}>
                  {fb.correct ? '✅ ' : '❌ '}
                  {fb.explanation}
                </p>
              )}
            </li>
          )
        })}
      </ol>

      {error && <div className="error-panel">{error}</div>}

      {!result ? (
        <button
          type="button"
          className="button button-primary"
          onClick={() => void submit()}
          disabled={!answeredAll || busy || !user}
        >
          {busy ? 'Grading…' : answeredAll ? 'Submit answers' : 'Answer every question first'}
        </button>
      ) : (
        <div className={result.passed ? 'result result-pass' : 'result result-fail'}>
          <strong>
            {result.score} / {result.total}
          </strong>
          {result.multiplier > 1 && (
            <span className="combo">
              🔥 {result.maxCombo} combo · ×{result.multiplier}
            </span>
          )}
          <span>+{result.xpGranted} XP</span>
          {result.leveledUp && <span className="levelup">LEVEL {result.game.level}!</span>}
        </div>
      )}
    </section>
  )
}
